import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { inr } from "../lib/format";

const field = "w-full bg-zinc-950 border border-zinc-800 px-3 py-3 text-sm focus:border-accent-ss placeholder:text-zinc-600";

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, pricing, coupon, setCoupon, clearCart, track } = useStore();
  const [f, setF] = useState({ email: "", phone: "", name: "", line1: "", line2: "", pincode: "", city: "", state: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { track("begin_checkout", {}); }, [track]);
  useEffect(() => { if (!cart.length && !submitted) navigate("/shop"); }, [cart.length, submitted, navigate]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const onPin = async (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
    setF((p) => ({ ...p, pincode: v }));
    if (v.length === 6) {
      try { await http.get(`/pincode/${v}`); } catch { toast.error("PIN not serviceable"); }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await http.post("/checkout", {
        items: cart.map((i) => ({ product_id: i.product_id, size: i.size, color: i.color, qty: i.qty })),
        email: f.email, phone: f.phone,
        shipping_address: { name: f.name, line1: f.line1, line2: f.line2, pincode: f.pincode, city: f.city, state: f.state },
        coupon_code: coupon || null, source: "direct",
      });
      track("purchase", { order_id: r.data.order_number });
      setSubmitted(true);
      clearCart();
      navigate(`/order/${r.data.order_number}`, { state: { order: r.data } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
      <h1 className="font-display text-5xl sm:text-6xl text-white uppercase tracking-tight mb-8">Checkout</h1>
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 space-y-8">
          <section>
            <h2 className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold mb-3">Contact</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} className={field} data-testid="checkout-email" />
              <input required placeholder="Phone" value={f.phone} onChange={set("phone")} className={field} data-testid="checkout-phone" />
            </div>
          </section>
          <section>
            <h2 className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold mb-3">Shipping Address</h2>
            <div className="space-y-3">
              <input required placeholder="Full name" value={f.name} onChange={set("name")} className={field} data-testid="checkout-name" />
              <input required placeholder="Address line 1" value={f.line1} onChange={set("line1")} className={field} data-testid="checkout-line1" />
              <input placeholder="Address line 2 (optional)" value={f.line2} onChange={set("line2")} className={field} />
              <div className="grid grid-cols-3 gap-3">
                <input required placeholder="PIN code" value={f.pincode} onChange={onPin} className={field} data-testid="checkout-pincode" />
                <input required placeholder="City" value={f.city} onChange={set("city")} className={field} data-testid="checkout-city" />
                <input required placeholder="State" value={f.state} onChange={set("state")} className={field} data-testid="checkout-state" />
              </div>
            </div>
          </section>
          <section>
            <h2 className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold mb-3">Payment</h2>
            <div className="border border-accent-ss p-4 flex items-center gap-3" style={{ borderColor: "var(--ss-accent)" }}>
              <span className="w-4 h-4 rounded-full border-2 border-accent-ss flex items-center justify-center"><span className="w-2 h-2 bg-accent-ss rounded-full" style={{ background: "var(--ss-accent)" }} /></span>
              <div>
                <p className="text-sm font-semibold text-white">Cash on Delivery</p>
                <p className="text-xs text-zinc-500">Pay in cash when your order arrives.</p>
              </div>
            </div>
          </section>
        </div>

        {/* Summary */}
        <div className="lg:col-span-2">
          <div className="bg-zinc-900/50 border border-zinc-800 p-5 space-y-4 lg:sticky lg:top-24">
            <h2 className="font-display text-2xl text-white tracking-wide">Order Summary</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {cart.map((it, i) => (
                <div key={i} className="flex gap-3">
                  <img src={it.image} alt={it.name} className="w-14 h-16 object-cover bg-zinc-900" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-100 line-clamp-1">{it.name}</p>
                    <p className="text-xs text-zinc-500">{[it.size, it.color].filter(Boolean).join(" / ")} × {it.qty}</p>
                  </div>
                  <span className="text-sm text-white">{inr(it.price * it.qty)}</span>
                </div>
              ))}
            </div>
            <div className="flex">
              <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="COUPON"
                className="flex-1 bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm uppercase" data-testid="checkout-coupon" />
            </div>
            {pricing?.coupon_error && <p className="text-xs text-red-400">{pricing.coupon_error}</p>}
            <div className="space-y-2 text-sm border-t border-zinc-800 pt-3">
              <div className="flex justify-between text-zinc-300"><span>Subtotal</span><span>{inr(pricing?.subtotal)}</span></div>
              {pricing?.discount > 0 && <div className="flex justify-between text-accent"><span>Discount</span><span>-{inr(pricing.discount)}</span></div>}
              <div className="flex justify-between text-zinc-300"><span>Shipping</span><span>{pricing?.shipping ? inr(pricing.shipping) : "FREE"}</span></div>
              {pricing?.cod_fee > 0 && <div className="flex justify-between text-zinc-300"><span>COD Fee</span><span>{inr(pricing.cod_fee)}</span></div>}
              <div className="flex justify-between text-lg font-bold text-white pt-2 border-t border-zinc-800"><span>Total</span><span data-testid="checkout-total">{inr(pricing?.total)}</span></div>
            </div>
            <button disabled={loading} className="btn-accent w-full py-4 text-sm font-bold uppercase tracking-wider disabled:opacity-60" data-testid="checkout-cod-submit-btn">
              {loading ? "Placing Order..." : "Place Order (COD)"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
